/* eslint-disable @nice-cxone/ccf/required-tsdoc */
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
export const CCF_RTIG_FEATURE_KEY = 'ccfrtig';

export interface ccfrtigState {
  overallSentiment: any;
  suggestionsList: any;
  guidanceScoreList: any;
  isSuggestionSummaryExpanded: boolean;
  isGuidanceScoreExpanded: boolean;
  resourceBaseUrl: string;
  showAgentAssist: boolean;
}

export const rtigInitialState: ccfrtigState = {
  overallSentiment: {
    guid: 'f090fad6-f869-4a87-a137-455160de6968',
    tag: 'Sentiment',
    image_src: 'meh',
    is_sentiment_score: true,
    percentage: -1,
  },

  suggestionsList: [],

  guidanceScoreList: [
    {
      guid: '6ca702e9-2996-4b55-b572-741a39286f13',
      tag: 'ThankForLoyality',
      type: 'SENTIMENT_MODEL',
      frienly_name: 'Thank For Loyalty',
      image_src: 'thank-for-loyalty',
      is_sentiment_score: false,
      percentage: 50,
      isValid: true,
      alertDescription: 'Thank the client for being loyal',
    },

    {
      guid: '7e30c59a-f91f-4326-96d1-276dd7644f9c',
      tag: 'BeEmpathetic',
      type: 'SENTIMENT_MODEL',
      frienly_name: 'Be Empathetic',
      image_src: 'be-empathetic',
      is_sentiment_score: false,
      percentage: 50,
      isValid: true,
      alertDescription: 'Understand the issue first',
    },
    {
      guid: '867e5e2b-b85f-486b-b715-cea7234ffb3c',
      tag: 'ActiveListening',
      type: 'SENTIMENT_MODEL',
      frienly_name: 'Active Listening',
      image_src: 'active-listening',
      is_sentiment_score: false,
      percentage: 50,
      isValid: true,
      alertDescription: 'Understand the issue first',
    },
    {
      guid: 'd32ef5e0-708c-40a4-bd4b-f391d36ecd1b',
      tag: 'EmpowerClient',
      type: 'SENTIMENT_MODEL',
      frienly_name: 'Empower Client',
      image_src: 'empower-client',
      is_sentiment_score: false,
      percentage: 50,
      isValid: true,
      alertDescription: 'Share helpful resources',
    },

    {
      guid: '142b1843-fd24-4718-a074-067984dbad63',
      tag: 'SpeechVelocity',
      type: 'SENTIMENT_MODEL',
      frienly_name: 'Speech Velocity',
      image_src: 'speech-velocity',
      is_sentiment_score: false,
      percentage: 50,
      isValid: true,
      alertDescription: "Speak more slowly, don't rush",
    },
    {
      guid: '49588d44-ce6b-4129-a20c-dbd4ad752688',
      tag: 'AskQuestions',
      type: 'SENTIMENT_MODEL',
      frienly_name: 'Ask Questions',
      image_src: 'ask-questions',
      is_sentiment_score: false,
      percentage: 50,
      isValid: true,
      alertDescription: 'Be curious and ask questions',
    },
    {
      guid: 'fa1f40a1-e7d7-4788-ad16-4b125571bacb',
      tag: 'SetExpectations',
      type: 'SENTIMENT_MODEL',
      frienly_name: 'Set Expectations',
      image_src: 'set-expectations',
      is_sentiment_score: false,
      percentage: 50,
      isValid: true,
      alertDescription: 'Explain the full process',
    },
    {
      guid: '59563b8c-35cf-4d90-8620-15cd0c5e6dc1',
      tag: 'NoInterruptons',
      type: 'SENTIMENT_MODEL',
      frienly_name: 'No Interruptions',
      image_src: 'no-interruptions',
      is_sentiment_score: false,
      percentage: 50,
      isValid: true,
      alertDescription: 'Wait for them to finish talking',
    },
    {
      guid: '8e6542e7-09e3-4350-89a0-d4d2cd733fa8',
      tag: 'DemonstrateOwnership',
      type: 'SENTIMENT_MODEL',
      frienly_name: 'Take Ownership',
      image_src: 'demonstrate-ownership',
      is_sentiment_score: false,
      percentage: 50,
      isValid: true,
      alertDescription: 'Own the solution end to end',
    },
    {
      guid: '70daadef-5d0a-4ee4-987e-900616ecef25',
      tag: 'BuildRapport',
      type: 'SENTIMENT_MODEL',
      frienly_name: 'Build Rapport',
      image_src: 'build-rapport',
      is_sentiment_score: false,
      percentage: 50,
      isValid: true,
      alertDescription: 'Be friendly, ask about their day!',
    },
    {
      guid: '39e68dd2-8785-4bec-8586-c1d85f707ffe',
      tag: 'IdentifySolution',
      type: 'SENTIMENT_MODEL',
      frienly_name: 'Identify Solution',
      image_src: 'identify-solution',
      is_sentiment_score: false,
      percentage: 50,
      isValid: true,
      alertDescription: 'Solved the issue successfully',
    },
  ],

  isSuggestionSummaryExpanded: true,
  isGuidanceScoreExpanded: true,
  resourceBaseUrl: '.',
  showAgentAssist: false,
};

export const ccfrtigSlice = createSlice({
  name: CCF_RTIG_FEATURE_KEY,
  initialState: rtigInitialState,
  reducers: {
    setShowAgentAssist(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        showAgentAssist: action.payload,
      };
    },
    // reset RTIG scores and clear notifications
    resetRtigScore(state) {
      state.suggestionsList = [];
      state.guidanceScoreList.map((item: any) => {
        item.percentage = 50;
        item.rangeOfMetricClass = 'default-range';
      });
      state.overallSentiment.percentage = -1;
      state.overallSentiment.image_src = 'meh';
      state.overallSentiment.rangeOfMetricClass = 'default-range';
      return state;
    },

    setRTIGStateForContact(state, action: PayloadAction<any>){
      return {
        ...state,
        overallSentiment: action.payload.overallSentiment,
        suggestionsList: action.payload.suggestionsList,
        guidanceScoreList: action.payload.guidanceScoreList,
      }

    },
    displayConfiguredMetricsOnly(state, action: PayloadAction<any>) {
      action.payload.configuredMetrics.map((metric: any, i: any) => {
        if (metric.enabled) {
          const guidanceMetric = state.guidanceScoreList.find(
            (item: any) => item.guid === metric.guid
          );
          if (guidanceMetric) {
            guidanceMetric.metricRange = getMerticRange(metric);
            guidanceMetric.isValid = metric.enabled;
          }
        }
      });
      return state;
    },

    updateGuidanceScores(state, action: PayloadAction<any>) {
      action.payload.enlightenResults.map((metric: any, i: any) => {
        if (metric.valid) {
          const guidanceMetric = state.guidanceScoreList.find(
            (item: any) => item.guid === metric.guid
          );
          const score = metric.score;
          const metricRange = getMerticRange(metric);
          const rangeOfMetricClass = getMerticRangeClass(score, metricRange);
          if (guidanceMetric) {
            // update guidance scores
            guidanceMetric.percentage = score;
            guidanceMetric.metricRange = metricRange;
            guidanceMetric.rangeOfMetricClass = rangeOfMetricClass;
          } else if (metric.guid === state.overallSentiment.guid) {
            // update overall sentiment
            state.overallSentiment.percentage = score;
            state.overallSentiment.rangeOfMetricClass = rangeOfMetricClass;

            if (
              state.overallSentiment.percentage <= 30 &&
              state.overallSentiment.percentage > 0
            ) {
              state.overallSentiment.image_src = 'sad';
            } else if (state.overallSentiment.percentage >= 71) {
              state.overallSentiment.image_src = 'smile';
            } else {
              state.overallSentiment.image_src = 'meh';
            }
          }
        }
      });
      return state;
    },

    showSuggestionNotifications(state, action: PayloadAction<any>) {
      const guidanceMetric = state.guidanceScoreList.find(
        (item: any) => item.guid === action.payload.notification.guid
      );
      if (guidanceMetric) {
        const suggestion: any = {};
        guidanceMetric.metricRange = getMerticRange(guidanceMetric);
        suggestion.label = guidanceMetric.frienly_name;
        suggestion.message = action.payload.notification.alertDescription;
        suggestion.imageSrc = guidanceMetric.image_src;
        suggestion.receptionDate = new Date();
        suggestion.percentage = action.payload.notification.score;
        suggestion.rangeOfMetricClass = getMerticRangeClass(
          suggestion.percentage,
          guidanceMetric.metricRange
        );
        suggestion.highLight = false;
        state.suggestionsList.unshift(suggestion);
      }
      return state;
    },
    updateSuggestionList(state, action: PayloadAction<any>) {
      return {
        ...state,
        suggestionsList: action.payload,
      };
    },
    setSuggestionSummaryExapnded(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isSuggestionSummaryExpanded: action.payload,
      };
    },
    setGuidanceScoreExapnded(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isGuidanceScoreExpanded: action.payload,
      };
    },
  },
});

const getMerticRangeClass = (score: number, rangeOfMetrics: any) => {
  let colorRangeClass = 'default-range';

  if (
    score > rangeOfMetrics.highThreshold.min &&
    score <= rangeOfMetrics.highThreshold.max
  ) {
    colorRangeClass = 'minimum-range';
  }

  if (
    score > rangeOfMetrics.mediumThreshold.min &&
    score <= rangeOfMetrics.mediumThreshold.max
  ) {
    colorRangeClass = 'medium-range';
  }

  if (
    score > rangeOfMetrics.lowThreshold.min &&
    score <= rangeOfMetrics.lowThreshold.max
  ) {
    colorRangeClass = 'maximum-range';
  }

  return colorRangeClass;
};

const getMerticRange = (metric: any) => {
  const defaultRangeOfMetric: any = {
    highThreshold: { min: 0, max: 30 },
    mediumThreshold: { min: 30, max: 70 },
    lowThreshold: { min: 70, max: 100 },
  };

  if (metric) {
    if (metric.highThreshold) {
      defaultRangeOfMetric.highThreshold.min = metric.highThreshold.min;
      defaultRangeOfMetric.highThreshold.max = metric.highThreshold.max;
    }

    if (metric.mediumThreshold) {
      defaultRangeOfMetric.mediumThreshold.min = metric.mediumThreshold.min;
      defaultRangeOfMetric.mediumThreshold.max = metric.mediumThreshold.max;
    }

    if (metric.lowThreshold) {
      defaultRangeOfMetric.lowThreshold.min = metric.lowThreshold.min;
      defaultRangeOfMetric.lowThreshold.max = metric.lowThreshold.max;
    }
  }

  return defaultRangeOfMetric;
};

export const ccfrtigActions = ccfrtigSlice.actions;
export const ccfrtigReducer = ccfrtigSlice.reducer;

 export const getccfrtigState = (rootState: { ccfrtig: ccfrtigState }) => {
  return rootState[CCF_RTIG_FEATURE_KEY];
};

export const selectresourceBaseUrl = createSelector(
  getccfrtigState,
  (state) => {
    return state.resourceBaseUrl;
  }
);

export const selectsuggestionsList = createSelector(
  getccfrtigState,
  (state) => {
    return state.suggestionsList;
  }
);

export const selectguidanceScoreList = createSelector(
  getccfrtigState,
  (state) => {
    return state.guidanceScoreList;
  }
);

export const selectIsSuggestionSummaryExpanded = createSelector(
  getccfrtigState,
  (state) => {
    return state.isSuggestionSummaryExpanded;
  }
);

export const selectIsGuidanceScoreExpanded = createSelector(
  getccfrtigState,
  (state) => {
    return state.isGuidanceScoreExpanded;
  }
);

export const selectOverallSentiment = createSelector(
  getccfrtigState,
  (state) => {
    return state.overallSentiment;
  }
);

export const getShowAgentAssist = createSelector(getccfrtigState, (state) => {
  return state.showAgentAssist;
});
